// layouts/BaseLayout.tsx
import React, { ReactNode, useState } from 'react';
import Sidebar from '@/components/layout/Sidebar';
import TopHeader from '@/components/layout/TopHeader';
import { AuthGuard } from '@/components/auth/AuthGuard';
import { DASHBOARD_MAX_WIDTH, useLayoutMode } from '@/components/layout/layoutConfig';
import { PageHeaderProvider } from '@/contexts/PageHeaderContext';
import { cn } from '@/lib/utils';

export type LayoutVariant = 'fullHeight' | 'scrollable';

interface BaseLayoutProps {
    children: ReactNode;
    variant?: LayoutVariant;
    className?: string;
    requireAuth?: boolean;
}

const BaseLayout: React.FC<BaseLayoutProps> = ({
                                                   children,
                                                   variant = 'scrollable',
                                                   className = '',
                                                   requireAuth = true
                                               }) => {
    // Sidebar states
    const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile toggle
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // Desktop minimize

    const handleSidebarClose = () => {
        setSidebarOpen(false);
    };

    const toggleSidebarCollapse = () => {
        setSidebarCollapsed(prev => !prev);
    };

    const layoutMode = useLayoutMode();
    const island = layoutMode === 'island';            // content becomes its own card
    const sidebarIsland = layoutMode !== 'classic';    // sidebar floats (both island modes)

    // PageHeaderProvider wraps the layout so any dashboard page can
    // inject custom header content into TopHeader via <PageHeader>.
    // Falls back to TopHeader's path-based title when no page injects.
    const content = (
        <PageHeaderProvider>
        <div className={cn('flex h-screen overflow-hidden', island ? 'bg-neutral-100' : 'bg-neutral-50')}>
            {/* Sidebar */}
            <Sidebar
                isOpen={sidebarOpen}
                isCollapsed={sidebarCollapsed}
                onClose={handleSidebarClose}
                onToggleCollapse={toggleSidebarCollapse}
            />

            {/* Main Content Area. Offset clears the sidebar — wider when the sidebar
                floats (island offset = 12px gap + width + 12px gap), classic otherwise.
                In full island mode the content also becomes its own floating card. */}
            <div
                className={cn(
                    'flex-1 flex flex-col transition-all duration-300 min-w-0',
                    sidebarIsland
                        ? (sidebarCollapsed ? 'lg:ml-[104px]' : 'lg:ml-[312px]')
                        : (sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'),
                    island && 'my-3 mx-3 rounded-2xl border-2 border-gray-100 bg-white shadow-sm overflow-hidden',
                )}
            >
                {/* Top Header */}
                <TopHeader
                    onMenuClick={() => setSidebarOpen(true)}
                    sidebarCollapsed={sidebarCollapsed}
                />

                {/* Page Content */}
                {variant === 'fullHeight' ? (
                    <main className={`flex-1 overflow-hidden ${className}`}>
                        {children}
                    </main>
                ) : (
                    <main className={`flex-1 overflow-y-auto ${className}`}>
                        <div
                            className={`p-4 sm:p-6 lg:p-8 ${
                                DASHBOARD_MAX_WIDTH ? `mx-auto w-full ${DASHBOARD_MAX_WIDTH}` : ''
                            }`}
                        >
                            {children}
                        </div>
                    </main>
                )}
            </div>
        </div>
        </PageHeaderProvider>
    );

    if (requireAuth) {
        return <AuthGuard>{content}</AuthGuard>;
    }

    return content;
};

export default BaseLayout;